import React, { useState, useMemo, useEffect } from 'react'
import { X, Package, Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import { HistoryItem } from '@/types'
import { formatClassNameForDisplay } from '@/utils/classNameUtils'
import { useCategoryContext } from '@/contexts/CategoryContext'
import { getClassColor, colorWithOpacity } from '@/utils/colorUtils'
import ThumbnailPreview from '@/components/ThumbnailPreview'

interface MobileSectionCreationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (sectionName: string) => void
  analysis: HistoryItem | null
  imageUrl: string | null
  isCreating: boolean
}

/**
 * Mobile dialog for creating a new section from an uploaded image
 * Shows the image preview, detected items, and a name input
 */
const MobileSectionCreationDialog: React.FC<MobileSectionCreationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  analysis,
  imageUrl,
  isCreating,
}) => {
  const { getClassCategory } = useCategoryContext()
  const [sectionName, setSectionName] = useState('')

  // Reset name when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSectionName('')
    }
  }, [isOpen])

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Extract unique detected classes from the image's detections
  const detectedClasses = useMemo(() => {
    if (!analysis?.detections) return []

    const classMap = new Map<string, number>()

    analysis.detections.forEach((detection) => {
      const className =
        detection.user_corrected_class ||
        (detection.top_classes && detection.top_classes.length > 0
          ? detection.top_classes[0].class_name || detection.top_classes[0].label
          : null)

      if (className) {
        classMap.set(className, (classMap.get(className) || 0) + 1)
      }
    })

    return Array.from(classMap.entries()).map(([className, count]) => ({
      class_name: className,
      expected_quantity: count,
    }))
  }, [analysis?.detections])

  if (!isOpen || !analysis) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (sectionName.trim()) {
      onConfirm(sectionName.trim())
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-in fade-in duration-200"
        onClick={!isCreating ? onClose : undefined}
      />

      {/* Dialog */}
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50',
          'bg-white rounded-t-2xl shadow-2xl',
          'animate-in slide-in-from-bottom duration-300',
          'safe-area-bottom max-h-[90vh] overflow-y-auto'
        )}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2 sticky top-0 bg-white">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="px-4 pb-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">
              Create New Section
            </h3>
            <p className="text-sm text-gray-500">
              Name this section based on the detected items
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isCreating}
            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 active:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          {/* Image Preview */}
          {imageUrl && (
            <div className="mb-4 rounded-lg overflow-hidden bg-gray-900">
              <img
                src={imageUrl}
                alt="Section preview"
                className="w-full max-h-48 object-contain"
              />
            </div>
          )}

          {/* Detected Items */}
          {detectedClasses.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Detected Items ({analysis.total_detections})
              </label>
              <div className="flex flex-wrap gap-1.5">
                {detectedClasses.map((item, index) => {
                  const category = getClassCategory(item.class_name)
                  const classColor = getClassColor(item.class_name, category)
                  return (
                    <ThumbnailPreview key={index} className={item.class_name}>
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer"
                        style={{
                          backgroundColor: colorWithOpacity(classColor, 0.2),
                          color: classColor,
                          border: `1px solid ${colorWithOpacity(classColor, 0.4)}`,
                        }}
                      >
                        {item.expected_quantity}x {formatClassNameForDisplay(item.class_name)}
                      </span>
                    </ThumbnailPreview>
                  )
                })}
              </div>
            </div>
          )}

          {detectedClasses.length === 0 && (
            <div className="mb-4 text-center py-4 text-gray-500">
              <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No items detected</p>
            </div>
          )}

          {/* Section Name Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Name
            </label>
            <input
              type="text"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              placeholder="e.g., Main Display, Shelf A, Window..."
              className={cn(
                'w-full px-4 py-3 border-2 rounded-xl',
                'focus:outline-none focus:ring-0 focus:border-primary-500',
                'text-gray-900 placeholder:text-gray-400',
                'transition-colors'
              )}
              disabled={isCreating}
              autoFocus
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className={cn(
                'flex-1 py-3 rounded-xl font-medium',
                'bg-gray-100 text-gray-700',
                'active:bg-gray-200 transition-colors',
                'disabled:opacity-50'
              )}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !sectionName.trim()}
              className={cn(
                'flex-1 py-3 rounded-xl font-medium',
                'bg-primary-600 text-white',
                'active:bg-primary-700 transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center justify-center gap-2'
              )}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Section'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default MobileSectionCreationDialog
